import React, { useEffect, useState } from "react";

const generateAvatarurl = process.env.REACT_APP_GENERATE_AVATAR_URL;
const domainpath = process.env.REACT_APP_API_DOMAIN_ENDPOINT;
const tzktApiUrl =
  process.env.REACT_APP_TZKT_API_ENDPOINT || "https://api.tzkt.io/v1";

const generateAvatar = (walletAddress) =>
  `${generateAvatarurl}/${walletAddress}.png?size=200x200`;

const shortenAddress = (address) =>
  address ? `${address.slice(0, 5)}...${address.slice(-4)}` : "";

const AuthorList = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSellerName = async (walletAddress) => {
    try {
      const res = await fetch(`${tzktApiUrl}/accounts/${walletAddress}`);
      if (!res.ok) throw new Error("TzKT lookup failed");
      const data = await res.json();
      return data.alias || "";
    } catch {
      return "";
    }
  };

  useEffect(() => {
    const fetchTopSellers = async () => {
      try {
        const res = await fetch(`${domainpath}/api/top-sellers`);
        const data = await res.json();
        const withNames = await Promise.all(
          data.map(async (seller) => {
            const alias = await fetchSellerName(seller.selleraddress);
            return { ...seller, name: alias };
          })
        );
        setSellers(withNames);
      } catch (err) {
        console.error("Error fetching top sellers:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTopSellers();
  }, []);

  const HeartSVG = () => (
    <svg
      className="w-4 h-4 text-red-500"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 
        4.42 3 7.5 3c1.74 0 3.41 0.81 
        4.5 2.09C13.09 3.81 14.76 3 16.5 
        3 19.58 3 22 5.42 22 8.5c0 
        3.78-3.4 6.86-8.55 11.54L12 
        21.35z"
      />
    </svg>
  );

  const UserSVG = () => (
    <svg
      className="w-4 h-4 text-gray-500"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 
        2.3-5 5 2.3 5 5 5zm0 
        2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z"
      />
    </svg>
  );

  const CheckSVG = () => (
    <i className="fa fa-check text-blue-500 w-6 h-4 text-green-700 absolute bottom-0 right-0 bg-white rounded-full border border-white"></i>
  );

  if (loading) {
    return (
      <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 space-y-4 animate-pulse">
        {Array.from({ length: 4 }).map((_, i) => (
          <li key={i} className="p-4 bg-white rounded-lg shadow flex gap-4">
            <div className="w-16 h-16 bg-gray-300 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-300 rounded w-1/2" />
              <div className="h-4 bg-gray-300 rounded w-1/3" />
            </div>
          </li>
        ))}
      </ol>
    );
  }

  return (
    <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {sellers.map((seller, i) => {
        const displayName =
          seller.username && seller.username !== seller.selleraddress
            ? seller.username
            : shortenAddress(seller.selleraddress);

        const rank = i + 1;
        const rankLabel = `${rank}${
          ["st", "nd", "rd"][((((rank + 90) % 100) - 10) % 10) - 1] || "th"
        }`;

        return (
          <li
            key={i}
            className="relative bg-white rounded-xl shadow-md p-4 flex items-center gap-4 transition hover:shadow-xl hover:scale-[1.02] duration-150 ease-in-out"
            title={`Rank: ${rankLabel}`}
          >
            {/* Rank Badge */}
            {/* <span className="absolute -top-2 -left-2 bg-black text-white text-xs font-bold px-2 py-1 rounded-full shadow">
              {rankLabel}
            </span> */}

            {/* Avatar */}
            <div className="relative w-16 h-16 shrink-0">
              <img
                src={
                  seller?.profileImage || generateAvatar(seller.selleraddress)
                }
                alt={seller.selleraddress}
                className="w-16 h-16 object-cover rounded-full border-2 border-gray-200"
              />
              <CheckSVG />
            </div>

            {/* Info */}
            <div className="flex flex-col min-w-0">
              <div
                onClick={() =>
                  window.open(`/seller/${seller.selleraddress}`, "_self")
                }
                className="truncate text-base font-semibold text-gray-800 cursor-pointer hover:underline"
                title={displayName}
              >
                {displayName}
              </div>
              <div
                className="text-sm text-gray-500 mb-1"
                title={`${(parseFloat(seller.amount) / 1_000_000).toFixed(
                  6
                )} ꜩ`}
              >
                {(parseFloat(seller.amount) / 1_000_000).toFixed(2)} ꜩ
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1" title="Followers">
                  <UserSVG />
                  <span>{seller.followers || 0}</span>
                </div>
                <div className="flex items-center gap-1" title="Total Likes">
                  <HeartSVG />
                  <span>{seller.totalLikes || 0}</span>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default AuthorList;
